import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { PRODUCTS } from "@/lib/mock/products";
import { useI18n } from "@/context/I18nContext";
import { getEvent } from "@/lib/mock/events";
import qrFuar from "@/assets/events/future-fish-2026/qr-fuar.svg";
import varsLogoWhite from "@/assets/vars-logo-white.png";

/**
 * The portrait screen on the trade stand.
 *
 * Not a web page in any normal sense. Nobody navigates here, nobody scrolls,
 * and nobody clicks — it is a monitor turned on its side at Fuar İzmir, running
 * for three days, read from two to four metres away by someone walking past.
 * Every decision below follows from that and from nothing else:
 *
 * - **Type is sized in `vh`, not `rem`.** The same markup has to be legible on
 *   a 43" 1080x1920 panel and on whoever's laptop is used to check it first.
 *   Viewport-relative sizing scales to the panel it is plugged into instead of
 *   assuming one.
 * - **The QR never leaves the screen.** It would have been tidier to give it a
 *   slide of its own, and it would have been wrong: a visitor decides to scan
 *   while looking at the product that interested them, and a code that is only
 *   there one slide in ten is a code they cannot find when they reach for it.
 * - **No interaction is required for anything.** There are no controls, because
 *   a passer-by will not use them and the stand team has better things to do.
 * - **It counts for nothing.** __root.tsx skips analytics for this route; a
 *   screen left running for a three-day fair would otherwise report itself as
 *   the site's most engaged visitor.
 *
 * Open it at `/stand` for English or `/tr/stand` for Turkish. The call to
 * action is bilingual either way — see the note on the QR panel.
 */
export const Route = createFileRoute("/stand")({
  head: () => ({
    meta: [{ title: "VARS — Stand Display" }],
  }),
  component: StandDisplay,
});

/**
 * Curated, in this order, and deliberately not "whatever order PRODUCTS is in".
 *
 * Narrowed on 24 September, on the day, at the owner's call: live feed and feed
 * additives only. Seafood export, salmon ova and fish meal came off the deck.
 *
 * That is an editorial decision about who walks past this particular stand, and
 * it is the right one to be able to make in one line — a fair audience is not
 * the same as a website audience, and a screen showing seven things the stand
 * actually wants to talk about beats one showing ten that merely exist.
 *
 * Seven slides at nine seconds is a sixty-three-second loop.
 */
const SLIDE_SLUGS = [
  "artemia",
  "decapsulated-dry-artemia-cysts",
  "super-fresh-chlorella-v12",
  "emerald-chlorella-powder",
  "enzyfish-coral",
  "enzyfish-silver",
  "enzyfish-gold",
] as const;

const CATEGORY_LABEL: Record<string, { tr: string; en: string }> = {
  "live-feed-aquaculture": { tr: "Canlı Yem", en: "Live Feed" },
  "hatchery-solutions": { tr: "Kuluçkahane", en: "Hatchery" },
  "feed-additives": { tr: "Yem Katkıları", en: "Feed Additives" },
  seafood: { tr: "Su Ürünleri İhracatı", en: "Seafood Export" },
};

const DEFAULT_SLIDE_MS = 9000;

/** Seconds per slide, overridable as `/stand?s=12`.
 *
 * The stand team cannot redeploy the site from a fairground, so the one number
 * they might genuinely want to change on the day is readable from the URL.
 * Clamped: a sub-second slideshow is a strobe, and a five-minute one is a
 * static poster. */
function slideMsFromUrl(): number {
  if (typeof window === "undefined") return DEFAULT_SLIDE_MS;
  const raw = new URLSearchParams(window.location.search).get("s");
  const secs = Number(raw);
  if (!raw || !Number.isFinite(secs)) return DEFAULT_SLIDE_MS;
  return Math.min(60, Math.max(3, secs)) * 1000;
}

function StandDisplay() {
  const { t, lang } = useI18n();
  const event = getEvent("future-fish-eurasia-2026");

  // `image` and `thumbnail` are both optional on Product, so a slide is only
  // a slide if it has artwork. A missing file would render as a broken image
  // icon two metres wide, which is worse than one fewer product.
  const slides = SLIDE_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug))
    .filter((p): p is (typeof PRODUCTS)[number] => Boolean(p))
    .map((p) => ({ product: p, art: p.thumbnail ?? p.image }))
    .filter((s): s is { product: (typeof PRODUCTS)[number]; art: string } => Boolean(s.art));

  const [index, setIndex] = useState(0);
  const [slideMs, setSlideMs] = useState(DEFAULT_SLIDE_MS);
  const [calmMotion, setCalmMotion] = useState(false);

  useEffect(() => setSlideMs(slideMsFromUrl()), []);

  /**
   * Reduced motion calms the transitions; it does NOT stop the rotation.
   *
   * On the homepage hero the same preference switches autoplay off entirely,
   * and that is right there — a visitor reading a page did not ask for moving
   * content. Here the rotation IS the content. Honouring the preference by
   * freezing the screen would turn a product showcase into a single static
   * slide for the whole fair, which serves nobody. What the preference does
   * remove is the drifting zoom and the cross-fade, which are decoration.
   */
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const sync = () => setCalmMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % slides.length), slideMs);
    return () => window.clearInterval(id);
  }, [slides.length, slideMs]);

  // Warm the next image so a slide never arrives half-painted. One ahead only:
  // the whole deck at once would be several megabytes on a stand's wifi.
  useEffect(() => {
    const next = slides[(index + 1) % slides.length];
    if (!next) return;
    const img = new Image();
    img.src = next.art;
  }, [index, slides]);

  /**
   * Keep the panel awake.
   *
   * A screen that blanks after ten minutes is the most likely way this display
   * fails at a fair, and it fails silently — nobody notices until a visitor is
   * standing in front of a black rectangle. The Wake Lock API is the only
   * in-page fix; browsers drop the lock whenever the tab is hidden, so it is
   * re-acquired on visibilitychange rather than requested once and trusted.
   *
   * Entirely best-effort. It is unavailable outside a secure context and in
   * some browsers, and the fallback is the operating system's own display
   * settings — which is why the operator note below says to disable sleep.
   */
  const lockRef = useRef<{ release: () => Promise<void> } | null>(null);
  useEffect(() => {
    type WakeLock = { request: (t: "screen") => Promise<{ release: () => Promise<void> }> };
    const wakeLock = (navigator as Navigator & { wakeLock?: WakeLock }).wakeLock;
    if (!wakeLock) return;

    let cancelled = false;
    const acquire = async () => {
      if (document.visibilityState !== "visible" || lockRef.current) return;
      try {
        const lock = await wakeLock.request("screen");
        if (cancelled) {
          void lock.release();
          return;
        }
        lockRef.current = lock;
      } catch {
        // Denied or unsupported. The OS display settings are the fallback.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
      else lockRef.current = null; // the browser has already released it
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void lockRef.current?.release().catch(() => {});
      lockRef.current = null;
    };
  }, []);

  // A browser cannot enter fullscreen without a gesture, so the whole screen is
  // the affordance: the operator taps it once when setting up. No visible
  // button, because it is not for visitors.
  const goFullscreen = () => {
    void document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const slide = slides[index];
  if (!slide) return null;
  const current = slide.product;

  const tp = (field: string, fallback: string) => {
    const key = `product.${current.slug}.${field}`;
    const hit = t(key);
    return hit === key ? fallback : hit;
  };

  // Bilingual, on one screen, rather than one language per URL.
  //
  // This is an international fair in Türkiye: a Turkish hatchery manager and a
  // German buyer read the same square metre of screen. The localized string
  // comes from the dictionary and the English one straight off the product
  // record, so no second dictionary has to be shipped to say both.
  const category = CATEGORY_LABEL[current.category];
  const categoryLabel = category ? `${category.tr} · ${category.en}` : "";
  const titleLocal = tp("title", current.title);
  const titleEn = current.title;
  const taglineLocal = tp("tagline", current.tagline);
  const tags = current.tags.slice(0, 3).map((tag, i) => tp(`tags.${i}`, tag));

  // Seafood art is photography; everything else is a packshot on a plain
  // ground. `cover` crops a photograph nicely and mangles a product bag, so
  // the fit follows what the picture actually is.
  const isPhoto = current.category === "seafood";

  return (
    <div
      onClick={goFullscreen}
      /* An explicit grid, not a stack of content-sized blocks.
         The first version let content decide its own height inside
         `overflow-hidden`, and the discount code fell off the bottom of a
         1080x1920 panel — invisible, and invisible in exactly the way signage
         cannot afford. These four rows always sum to the viewport: only the
         stage flexes, and it is the row that can afford to. */
      className="grid h-[100dvh] w-screen grid-cols-1 grid-rows-[11vh_minmax(0,1fr)_auto_25vh] overflow-hidden bg-navy text-white select-none"
    >
      {/* Progress. A bar rather than dots: dots imply something to click, and
          there is nothing here to click. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-30 h-[0.6vh] bg-white/10">
        <div
          key={`${index}-${slideMs}`}
          className="h-full bg-mint"
          style={{
            animation: calmMotion ? undefined : `stand-progress ${slideMs}ms linear forwards`,
            width: calmMotion ? `${((index + 1) / slides.length) * 100}%` : undefined,
          }}
        />
      </div>

      {/* A soft brand glow behind everything, so the navy is not a flat field
          on a large panel where flat reads as cheap. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_70%_at_50%_0%,rgba(0,153,102,0.28),transparent_60%)]"
      />

      {/* ── Masthead ── */}
      <header className="relative z-20 flex min-w-0 items-center justify-between gap-[2vh] px-[4vh]">
        <img src={varsLogoWhite} alt="VARS" className="h-[4.6vh] w-auto object-contain" />
        <div className="min-w-0 text-end">
          <div className="font-display text-[2.1vh] font-bold uppercase tracking-[0.2em] text-mint">
            <span className="whitespace-nowrap">
              {event?.hall ? `Hall ${event.hall} · ` : ""}
              {event?.stand}
            </span>
          </div>
          <div className="mt-[0.3vh] text-[1.4vh] font-semibold uppercase tracking-[0.14em] text-white/50">
            Future Fish Eurasia 2026
          </div>
        </div>
      </header>

      {/* ── The product, actually visible ──
          The first version used the image as a full-bleed background under
          three gradient ramps, which is a good way to present a photograph and
          a terrible way to present a product: every slide came out a dark blur
          on a screen whose entire job is showing what VARS sells. It sits in
          its own lit panel now. */}
      <div className="relative z-20 mx-[4vh] mt-[1vh] min-h-0">
        <div className="relative h-full w-full overflow-hidden rounded-[2.4vh] bg-white shadow-2xl ring-1 ring-white/15">
          <img
            key={current.slug}
            src={slide.art}
            alt=""
            aria-hidden="true"
            className={`h-full w-full ${isPhoto ? "object-cover" : "object-contain p-[2.5vh]"}`}
            style={{
              animation:
                calmMotion || !isPhoto ? undefined : "stand-kenburns 18s ease-out forwards",
            }}
          />
          {categoryLabel && (
            <div className="absolute start-[2vh] top-[2vh] rounded-full bg-navy/90 px-[2vh] py-[0.9vh] text-[1.6vh] font-bold uppercase tracking-[0.2em] text-mint backdrop-blur-sm">
              {categoryLabel}
            </div>
          )}
        </div>
      </div>

      {/* ── Name and specs ── */}
      <div
        key={`copy-${current.slug}`}
        className={`relative z-20 px-[4vh] pt-[2.4vh] ${calmMotion ? "" : "animate-fade-in-up"}`}
        style={{ animationDuration: "600ms" }}
      >
        {/* Two lines, hard-capped. "Artemia Cysts (Brine Shrimp Eggs)" ran to
            three lines at the size this started at and pushed everything below
            it off the screen. */}
        <h1 className="line-clamp-2 font-display text-[3.4vh] font-bold leading-[1.1] tracking-tight text-balance text-white">
          {titleLocal}
        </h1>
        {/* Only when it differs — on the English page the two are the same
            string, and printing it twice would read as a bug. */}
        {titleEn !== titleLocal && (
          <p className="mt-[0.5vh] line-clamp-1 font-display text-[2.3vh] font-bold text-mint">
            {titleEn}
          </p>
        )}

        {current.latinName && (
          <p className="mt-[0.6vh] text-[2vh] italic text-mint/80">{current.latinName}</p>
        )}

        <p className="mt-[1.4vh] line-clamp-3 max-w-[96%] text-[2vh] leading-snug text-white/80">
          {taglineLocal}
        </p>

        {tags.length > 0 && (
          <div className="mt-[1.8vh] flex flex-wrap gap-[1vh]">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-lg border border-white/20 bg-white/10 px-[1.6vh] py-[0.8vh] text-[1.7vh] font-semibold whitespace-nowrap text-white"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── The ask: visitor registration. Always on screen, bilingual, and
             the only thing here trying to get something from the reader.

             This panel offered a fair discount and a promo code until the goal
             was restated as registration. Both are gone — a discount is a
             different conversation from "tell us who you are", and a screen
             that asks for two things gets neither.

             Hardcoded Turkish and English rather than routed through the locale
             files, on purpose: this panel is bilingual at once, which no single
             locale can express. A Turkish visitor and a German buyer read the
             same square metre of screen, and the call to action is the one
             thing neither can afford to miss. ── */}
      <footer className="relative z-20 m-[3vh] mt-[2vh] flex items-center gap-[2.2vh] rounded-[2.2vh] bg-white px-[2.6vh] py-[2vh] text-navy shadow-2xl">
        <img
          src={qrFuar}
          alt="varsco.com/fuar"
          className="h-[15vh] w-[15vh] shrink-0"
          /* No rounding and no padding: quiet-zone and corner integrity are
             what a scanner needs, and a clipped finder pattern is a code that
             does not read. */
        />
        <div className="min-w-0 flex-1">
          <div className="font-display text-[2.7vh] font-bold leading-tight whitespace-nowrap text-navy">
            Ziyaretçi kaydı yapın
          </div>
          <div className="font-display text-[2.1vh] font-bold leading-tight whitespace-nowrap text-primary">
            Register your visit
          </div>
          <p className="mt-[0.9vh] text-[1.6vh] font-bold whitespace-nowrap text-navy/55">
            Karekodu okutun · Scan the code
          </p>
          {/* The short URL takes the slot the discount code used to hold.
              A visitor without a free hand for their phone can still be told
              where to go, which serves a registration goal; a promo code did
              not. */}
          <span className="mt-[1.2vh] inline-flex items-baseline gap-[1.2vh] rounded-lg bg-navy px-[1.8vh] py-[1vh] whitespace-nowrap">
            <span className="font-display text-[2.2vh] font-bold tracking-wide text-white">
              varsco.com/fuar
            </span>
          </span>
        </div>
      </footer>
    </div>
  );
}
